/**
 * La frise d'un circuit — M7 (retour Manon, mi-parcours).
 *
 * « Une vue graphique du circuit — les étapes, les flèches — avec à chaque
 *   étape la personne, la date et le document. »
 *
 * Chaque étape du parcours, dans l'ordre :
 *   · FRANCHIE  — ✓, qui l'a franchie, quand, et sa pièce (originale et/ou
 *                 signée à la main, M9) ouvrable d'un clic ;
 *   · COURANTE  — mise en avant ;
 *   · À VENIR   — estompée. Une étape finale non atteinte alors que le
 *                 parcours est terminé ailleurs (ex. « Refusé » quand on a
 *                 « Accepté ») est masquée : ce n'était pas le chemin suivi.
 * Rendue comme une LISTE ORDONNÉE : lisible au lecteur d'écran (RGAA), et la
 * flèche n'est qu'un décor.
 */
import { api, ApiException } from '@/lib/api';
import { useState } from 'react';

interface Etape { id: string; libelle: string; ordre: number; estFinal: boolean }
interface Evt {
  id: string; versEtat: string; versEtatId?: string; par: string | null; createdAt: string;
  aPiece: boolean; pieceNom?: string | null; aDocumentSigne?: boolean; documentSigneNom?: string | null;
}

const fr = (iso: string) => new Date(iso).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'Europe/Paris' });

export function CircuitFrise({ instanceId, etats, etatCourantId, historique }: {
  instanceId: string; etats: Etape[]; etatCourantId: string; historique: Evt[];
}) {
  const [err, setErr] = useState<string | null>(null);
  // Le DERNIER passage par chaque étape (un circuit peut revenir en arrière).
  const passage = new Map<string, Evt>();
  for (const e of historique) {
    const cle = e.versEtatId ?? etats.find((x) => x.libelle === e.versEtat)?.id;
    if (cle) passage.set(cle, e);
  }
  const courant = etats.find((x) => x.id === etatCourantId);
  const termine = !!courant?.estFinal;
  const visibles = [...etats].sort((a, b) => a.ordre - b.ordre)
    .filter((e) => !(termine && e.estFinal && e.id !== etatCourantId && !passage.has(e.id)));

  async function ouvrir(evtId: string, quelle: 'piece' | 'signe') {
    setErr(null);
    try { const { url } = await api.workflows.lienPiece(instanceId, evtId, quelle); window.open(url, '_blank', 'noopener'); }
    catch (e) { setErr(e instanceof ApiException ? e.payload.error : 'Document indisponible'); }
  }

  return (
    <div className="overflow-x-auto">
      <ol className="flex min-w-max items-stretch gap-1 py-1" aria-label="Étapes du circuit">
        {visibles.map((e, i) => {
          const p = passage.get(e.id);
          const estCourant = e.id === etatCourantId;
          const etat = estCourant ? 'courante' : p ? 'franchie' : 'avenir';
          return (
            <li key={e.id} className="flex items-stretch gap-1" aria-current={estCourant ? 'step' : undefined}>
              {i > 0 && <span aria-hidden="true" className="self-center text-ink-subtle">→</span>}
              <div className={[
                'w-40 rounded-r-md border px-2.5 py-1.5 text-2xs',
                etat === 'courante' ? 'border-tech bg-tech-tint text-ink ring-1 ring-tech'
                  : etat === 'franchie' ? 'border-success/40 text-ink' : 'border-border text-ink-subtle opacity-70',
              ].join(' ')}>
                <p className="truncate text-xs font-medium" title={e.libelle}>
                  {etat === 'franchie' ? '✓ ' : ''}{e.libelle}
                  <span className="sr-only">{etat === 'courante' ? ' (étape en cours)' : etat === 'franchie' ? ' (franchie)' : ' (à venir)'}</span>
                </p>
                {p && <p className="truncate">{p.par ?? '—'}</p>}
                {p && <p className="text-ink-subtle">{fr(p.createdAt)}</p>}
                {p?.aPiece && (
                  <button type="button" className="mt-0.5 block truncate text-accent hover:underline" title={p.pieceNom ?? 'Pièce'}
                    onClick={() => void ouvrir(p.id, 'piece')}>📎 {p.pieceNom ?? 'Pièce'}</button>
                )}
                {p?.aDocumentSigne && (
                  <button type="button" className="block truncate text-accent hover:underline" title={p.documentSigneNom ?? 'Document signé'}
                    onClick={() => void ouvrir(p.id, 'signe')}>✍ Signé</button>
                )}
              </div>
            </li>
          );
        })}
      </ol>
      {err && <p className="text-2xs text-urgent" role="alert">{err}</p>}
    </div>
  );
}
